import {mapiqApi} from '@lib/infrastructure';
import {AppLanguage, storageKeys, withPayloadType} from '@lib/utils';
import {createAction, createAsyncThunk} from '@reduxjs/toolkit';
import {AppThunkApiConfig} from '../../createStore';
import {Settings} from './types';
import {UserSettings} from '../entities';

/**
 * Sets today as ISO string in the store.
 */
export const setStartOfToday = createAction('settings/startOfTodaySet', withPayloadType<string>());

export const setAppLanguage = createAction('settings/appLanguageSet', withPayloadType<AppLanguage>());

export const changeAppLanguage = createAsyncThunk<AppLanguage, AppLanguage, AppThunkApiConfig>(
  'settings/changeAppLanguage',
  async (language, {extra}) => {
    await extra.i18n.changeLanguage(language);
    return language;
  },
);

export const loadSettings = createAsyncThunk<Settings, void, AppThunkApiConfig>(
  'settings/loadSettings',
  async (_, {extra}) => {
    const client = extra.apiClient;
    const {getSettings} = mapiqApi(client);
    const response = await getSettings();
    return response;
  },
);

export const loadWasDeskBookingOnboardingShown = createAsyncThunk<boolean, void, AppThunkApiConfig>(
  'settings/loadWasDeskBookingOnboardingShown',
  async (_, {extra}) => {
    const {getItem} = extra.localStore;
    const result = await getItem(storageKeys.current.wasDeskBookingOnboardingShownKey);
    return result === 'true';
  },
);

export const markDeskBookingOnboardingAsShown = createAsyncThunk<void, void, AppThunkApiConfig>(
  'settings/markDeskBookingOnboardingAsShown',
  async (_, {extra}) => {
    const {setItem} = extra.localStore;
    await setItem(storageKeys.current.wasDeskBookingOnboardingShownKey, 'true');
  },
);

export const loadWasUserOnboardedToNotifications = createAsyncThunk<boolean, void, AppThunkApiConfig>(
  'settings/loadWasUserOnboardedToNotifications',
  async (_, {extra}) => {
    const {getItem} = extra.localStore;
    const result = await getItem(storageKeys.current.wasUserOnboardedToNotificationsKey);
    return result === 'true';
  },
);

export const markUserAsOnboardedToNotifications = createAsyncThunk<void, void, AppThunkApiConfig>(
  'settings/markUserAsOnboardedToNotifications',
  async (_, {extra}) => {
    const {setItem} = extra.localStore;
    await setItem(storageKeys.current.wasUserOnboardedToNotificationsKey, 'true');
  },
);

export const loadHasUserClosedNotificationReminder = createAsyncThunk<boolean, void, AppThunkApiConfig>(
  'settings/loadHasUserClosedNotificationReminder',
  async (_, {extra}) => {
    const {localStore} = extra;
    const hasUserClosed = await localStore.getItem(storageKeys.current.hasUserClosedPushNotificationReminderKey);
    return hasUserClosed === `${true}`;
  },
);

export const markPushNotificationReminderAsClosed = createAsyncThunk<void, void, AppThunkApiConfig>(
  'settings/markPushNotificationReminderAsClosed',
  async (_, {extra}) => {
    const {localStore} = extra;
    await localStore.setItem(storageKeys.current.hasUserClosedPushNotificationReminderKey, `${true}`);
  },
);

export const loadUserSettings = createAsyncThunk<UserSettings, void, AppThunkApiConfig>(
  'settings/loadUserSettings',
  async (_, {extra}) => {
    const client = extra.apiClient;
    const {getUserSettings} = mapiqApi(client);
    return await getUserSettings();
  },
);
